import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import StorageProviderService from '@utility/services/storage-provider';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';
import { ToastMessage } from '@utility/toast';
import {
  invoicePdfName,
  type InvoiceLog,
  type ProviderInvoiceDetail,
} from '../lib/invoices';
import { providerKeys } from '../lib/query-keys';
import { pagedFilter, usePagedFilter } from './use-paged-filter';

/** One invoice the WMS provider issued. */
export function useProviderInvoice(providerId: string, invoiceId: string) {
  const query = useQuery({
    queryKey: providerKeys.invoice(providerId, invoiceId),
    queryFn: async () => {
      const result = await StorageProviderService.wmsProviderInvoiceDetail(
        providerId,
        invoiceId,
      );
      return (result?.data ?? null) as ProviderInvoiceDetail | null;
    },
    enabled: !!providerId && !!invoiceId,
  });

  return {
    data: query.data ?? null,
    loading: query.isFetching,
    error: query.error?.message ?? '',
  };
}

/** The invoice's history, ten to a page, searchable by category, service and message. */
export function useInvoiceLogs(providerId: string, invoiceId: string) {
  const paged = usePagedFilter(pagedFilter(), filter => filter);
  const { applied } = paged;

  const query = useQuery({
    queryKey: providerKeys.invoiceLogs(providerId, invoiceId, applied),
    queryFn: async () => {
      const result = await StorageProviderService.getWMSInvoicesLogs(
        providerId,
        invoiceId,
        applied,
      );
      return {
        rows: (result?.data?.logs ?? []) as InvoiceLog[],
        total: (result?.data?.total ?? 0) as number,
      };
    },
    enabled: !!providerId && !!invoiceId && !!applied,
    placeholderData: keepPreviousData,
  });

  return {
    ...paged,
    data: query.data?.rows ?? [],
    totalData: query.data?.total ?? 0,
    loading: !applied || query.isFetching,
  };
}

function fetchPdf(providerId: string, invoiceId: string): Promise<Blob> {
  return StorageProviderService.getInvoicePDF(
    providerId,
    invoiceId,
    { responseType: 'blob' },
    { 'Content-Type': 'application/pdf' },
  );
}

/** The invoice as a PDF (saved or opened to print) and its history as CSV. */
export function useInvoiceFiles(
  providerId: string,
  invoice: ProviderInvoiceDetail | null,
) {
  const onError = (error: unknown) => ToastMessage.apiError(error);

  const download = useMutation({
    mutationFn: async () => {
      if (!invoice) return;
      const url = URL.createObjectURL(await fetchPdf(providerId, invoice._id));
      const link = document.createElement('a');
      link.href = url;
      link.download = invoicePdfName(invoice);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    onError,
  });

  /* The browser's own viewer prints it; the URL stays alive for that tab. */
  const print = useMutation({
    mutationFn: async () => {
      if (!invoice) return;
      window.open(URL.createObjectURL(await fetchPdf(providerId, invoice._id)));
    },
    onError,
  });

  const exportLogs = useMutation({
    mutationFn: async () => {
      if (!invoice) return;
      const csv = await StorageProviderService.exportWMSInvoicesLogs(
        invoice._id,
      );
      CommonUtility.downloadCSV(
        csv,
        `${invoice.invoiceNumber ?? ''}-${DateUtility.formatDate(
          new Date(),
          DateFormat.dateTime,
        )}`,
      );
    },
    onError,
  });

  return { download, print, exportLogs };
}
